export type Step = 'animal' | 'status' | 'person' | 'unit'

export type Assistance =
  | { type: 'PET' }
  | { type: 'SERVICE'; requiredForDisability: boolean; taskDescription: string }
  | { type: 'ESA' }

export interface ClassifyAnswers {
  neededForDisability: boolean | null // Q1
  taskTrainedDog: boolean | null // Q2 - only meaningful for dogs
}

// Derives the assistance category from the guided answers + the animal's
// species. Returns null when not enough is answered to decide yet (keeps the
// Next button gated). taskDescription starts empty; StatusStep fills it for the
// SERVICE path. Under the ADA a service animal is a dog, so a non-dog can never
// reach SERVICE.
export function classifyAssistance(
  ans: ClassifyAnswers,
  species: 'DOG' | 'CAT' | 'OTHER' | null,
): Assistance | null {
  if (ans.neededForDisability === null) return null
  if (!ans.neededForDisability) return { type: 'PET' }
  if (species !== 'DOG') return { type: 'ESA' }
  if (ans.taskTrainedDog === null) return null
  if (ans.taskTrainedDog) {
    return { type: 'SERVICE', requiredForDisability: true, taskDescription: '' }
  }
  return { type: 'ESA' }
}

export interface Draft {
  animal: {
    name: string
    species: 'DOG' | 'CAT' | 'OTHER' | null
    speciesOther: string
    breedId: number | null
    birthdate: string
    color: string
  }
  assistance: Assistance | null
  person: {
    firstName: string
    lastName: string
    email: string
  }
  unitId: number | null
}

export function emptyDraft(): Draft {
  return {
    animal: { name: '', species: null, speciesOther: '', breedId: null, birthdate: '', color: '' },
    assistance: null,
    person: { firstName: '', lastName: '', email: '' },
    unitId: null,
  }
}

// Same regex the backend's Type.String({ format: 'email' }) would accept - good enough
// for client-side gating. The server re-validates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function canGoNext(draft: Draft, step: Step): boolean {
  switch (step) {
    case 'animal':
      return draft.animal.name.trim().length > 0 && draft.animal.species !== null
    case 'status': {
      const a = draft.assistance
      if (!a) return false
      // Service path requires BOTH the affirmative disability assertion and a
      // task description before the applicant can continue.
      if (a.type === 'SERVICE')
        return a.requiredForDisability && a.taskDescription.trim().length > 0
      return true
    }
    case 'person':
      return (
        draft.person.firstName.trim().length > 0 &&
        draft.person.lastName.trim().length > 0 &&
        EMAIL_RE.test(draft.person.email)
      )
    case 'unit':
      return draft.unitId !== null
  }
}

export interface Prefill {
  applicant?: { firstName?: string; lastName?: string; email?: string }
  unitId?: number
  pet?: { name?: string; species?: 'DOG' | 'CAT' | 'OTHER'; breedId?: number; color?: string; birthdate?: string }
}

export function seedDraft(prefill: Prefill): Draft {
  const d = emptyDraft()
  if (prefill.applicant) {
    if (prefill.applicant.firstName) d.person.firstName = prefill.applicant.firstName
    if (prefill.applicant.lastName) d.person.lastName = prefill.applicant.lastName
    if (prefill.applicant.email) d.person.email = prefill.applicant.email
  }
  if (prefill.unitId != null) d.unitId = prefill.unitId
  if (prefill.pet) {
    if (prefill.pet.name) d.animal.name = prefill.pet.name
    if (prefill.pet.species) d.animal.species = prefill.pet.species
    if (prefill.pet.breedId != null) d.animal.breedId = prefill.pet.breedId
    if (prefill.pet.color) d.animal.color = prefill.pet.color
    if (prefill.pet.birthdate) d.animal.birthdate = prefill.pet.birthdate
  }
  return d
}

// A step counts as complete for telescoping when its required fields are valid. The status
// (classifier) step is never prefilled, so it is never complete and is always shown.
export function isStepComplete(step: Step, draft: Draft): boolean {
  if (step === 'status') return false
  return canGoNext(draft, step)
}

const STEP_ORDER: Step[] = ['animal', 'status', 'person', 'unit']

export function firstIncompleteStep(draft: Draft): Step {
  return STEP_ORDER.find((s) => !isStepComplete(s, draft)) ?? 'animal'
}

export function hasAnyPrefill(prefill: Prefill | undefined): boolean {
  if (!prefill) return false
  return Boolean(
    (prefill.applicant && Object.keys(prefill.applicant).length > 0) ||
      prefill.unitId != null ||
      (prefill.pet && Object.keys(prefill.pet).length > 0),
  )
}

export interface IntakeBody {
  animal: {
    name: string
    species: 'DOG' | 'CAT' | 'OTHER'
    speciesOther?: string
    breedId?: number
    birthdate?: string
    color?: string
  }
  assistance?:
    { type: 'SERVICE'; requiredForDisability: boolean; taskDescription: string } | { type: 'ESA' }
  person: {
    firstName: string
    lastName: string
    email: string
  }
  unitId: number
}

export function toIntakeBody(draft: Draft): IntakeBody {
  if (draft.unitId === null) {
    throw new Error('toIntakeBody called with null unitId - form should have blocked this')
  }
  if (draft.animal.species === null) {
    throw new Error('toIntakeBody called with null species - form should have blocked this')
  }
  const animal: IntakeBody['animal'] = {
    name: draft.animal.name.trim(),
    species: draft.animal.species,
  }
  if (draft.animal.species === 'OTHER' && draft.animal.speciesOther.trim()) {
    animal.speciesOther = draft.animal.speciesOther.trim()
  }
  if (draft.animal.species !== 'OTHER' && draft.animal.breedId !== null) {
    animal.breedId = draft.animal.breedId
  }
  if (draft.animal.birthdate) animal.birthdate = draft.animal.birthdate
  if (draft.animal.color.trim()) animal.color = draft.animal.color.trim()

  const a = draft.assistance
  const assistance =
    a && a.type === 'SERVICE'
      ? {
          type: 'SERVICE' as const,
          requiredForDisability: a.requiredForDisability,
          taskDescription: a.taskDescription.trim(),
        }
      : a && a.type === 'ESA'
        ? { type: 'ESA' as const }
        : undefined

  return {
    animal,
    ...(assistance ? { assistance } : {}),
    person: {
      firstName: draft.person.firstName.trim(),
      lastName: draft.person.lastName.trim(),
      email: draft.person.email.trim(),
    },
    unitId: draft.unitId,
  }
}
